import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth }          from "./hooks/useAuth";
import { useHabitOS }       from "./hooks/useHabitOS";
import { useInsights }      from "./hooks/useInsights";
import { useDarkMode, LIGHT } from "./hooks/useDarkMode";
import { useTranslation }   from "./hooks/useTranslation";
import { AuthGate }         from "./components/AuthGate";
import { DiscoverTab }      from "./components/DiscoverTab";
import { InsightsTab }      from "./components/InsightsTab";
import { ProfileSheet }     from "./components/ProfileSheet";
import { OnboardingFlow }   from "./components/OnboardingFlow";
import { AgentChat }        from "./components/AgentChat";
import { PersonalitySelector } from "./components/AgentChat";
import { PWAInstallBanner } from "./components/PWAInstallBanner";
import { ROUTINES, ROUTINE_CATEGORIES, getRoutinesByCategory } from "./routines";
import { HABIT_LIBRARY } from "./habitLibrary";
import { ConsentGate } from "./components/ConsentGate";

// Z wird dynamisch gesetzt — Fallback auf LIGHT für Sub-Komponenten
// die außerhalb des App-Kontexts rendern
let Z = LIGHT;

// ─────────────────────────────────────────────
// DATUM-HELPERS
// ─────────────────────────────────────────────
const toKey = (date) => date.toISOString().slice(0, 10);
const today = () => toKey(new Date());

function getLast7Days() {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    days.push(toKey(d));
  }
  return days;
}

function dayLabel(dateStr) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short" }).slice(0, 2);
}

function getGreeting(t) {
  const h = new Date().getHours();
  return h < 12 ? t("greeting_morning") : h < 18 ? t("greeting_afternoon") : t("greeting_evening");
}

// ─────────────────────────────────────────────
// HABIT STREAK BERECHNUNG (client-seitig)
// ─────────────────────────────────────────────
function computeHabitStreaks(history, habitIds) {
  const todayStr = today();
  const streaks = {};
  for (const id of habitIds) {
    let streak = 0;
    for (let i = 0; i < 90; i++) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = toKey(d);
      const row = history[key];
      const done = row?.done_habits?.includes(id);
      if (done) { streak++; }
      else if (key !== todayStr) { break; }
    }
    streaks[id] = streak;
  }
  return streaks;
}

// ─────────────────────────────────────────────
// KONFETTI
// ─────────────────────────────────────────────
function spawnConfetti(container) {
  if (!container) return;
  const colors = [Z.primary, Z.secondary, Z.primaryFixedDim, "#a68e55", Z.onSecondaryContainer];
  container.style.position = "relative";
  const dots = [];
  for (let i = 0; i < 30; i++) {
    const d = document.createElement("div");
    const s = 5 + Math.random() * 5;
    d.style.cssText = `position:absolute;width:${s}px;height:${s}px;border-radius:50%;background:${colors[i%colors.length]};left:${10+Math.random()*80}%;top:${Math.random()*20}px;pointer-events:none;z-index:50;animation:zenithConfetti ${1.2+Math.random()*0.8}s ease-in forwards;animation-delay:${Math.random()*0.4}s;`;
    container.appendChild(d);
    dots.push(d);
  }
  setTimeout(() => dots.forEach((d) => d.remove()), 2600);
}

// ─────────────────────────────────────────────
// KALENDER
// ─────────────────────────────────────────────
const DAY_STATUS = {
  complete:   { bg: Z.primaryFixed,        border: Z.primaryFixedDim, text: Z.primary,  symbol: "✓" },
  freeze:     { bg: "#e8f4fd",             border: "#93C5FD",          text: "#1D4ED8",  symbol: "❄" },
  grace:      { bg: "#fffbeb",             border: "#fcd34d",          text: "#92400e",  symbol: "~" },
  missed:     { bg: Z.errorContainer,      border: "#fca5a5",          text: Z.error,    symbol: "✗" },
  incomplete: { bg: Z.surfaceContainerLow, border: Z.outlineVariant,   text: Z.outline,  symbol: "·" },
  empty:      { bg: Z.surfaceContainerLow, border: Z.outlineVariant,   text: Z.outline,  symbol: "·" },
};

function MiniCalendar({ history }) {
  const days = getLast7Days();
  const todayStr = today();
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {days.map((day) => {
        const isToday = day === todayStr;
        const s = DAY_STATUS[history[day] || "empty"] || DAY_STATUS.empty;
        return (
          <div key={day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 10, color: isToday ? Z.primary : Z.outline, fontWeight: isToday ? 700 : 400, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {dayLabel(day)}
            </span>
            <div style={{ width: "100%", aspectRatio: "1", borderRadius: 10, background: s.bg, border: `${isToday && !history[day] ? `2px solid ${Z.primary}` : `1px solid ${s.border}`}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: s.text, fontWeight: 600 }}>
              {s.symbol}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// PROGRESS RING
// ─────────────────────────────────────────────
function ProgressRing({ pct, size = 80, stroke = 7 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={Z.surfaceContainerHigh} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={Z.primary} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={circ - (pct / 100) * circ}
        strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.5s ease" }} />
    </svg>
  );
}

// ─────────────────────────────────────────────
// FREEZE BAR
// ─────────────────────────────────────────────
const MAX_FREEZES = 3;
function FreezeBar({ freezes, earnedToday }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#e8f4fd", borderRadius: 14, border: "1px solid #bfdbfe" }}>
      <span style={{ fontSize: 18 }}>❄️</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1d4ed8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Streak Freezes</div>
        <div style={{ fontSize: 11, color: "#60a5fa", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Verdient durch alle Morning-Habits</div>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {Array.from({ length: MAX_FREEZES }).map((_, i) => (
          <div key={i} style={{ width: 24, height: 24, borderRadius: 6, background: i < freezes ? "#3b82f6" : "#dbeafe", border: `1px solid ${i < freezes ? "#2563eb" : "#bfdbfe"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
            {i < freezes ? "❄" : "·"}
          </div>
        ))}
        {earnedToday && <span style={{ fontSize: 11, color: Z.primary, fontWeight: 600, marginLeft: 4 }}>+1!</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// HABIT ITEM (Swipe-to-Delete + Streak-Badge)
// ─────────────────────────────────────────────
function HabitItem({ habit, done, streak, onToggle, onDelete, theme, isCustom }) {
  const [swipeX, setSwipeX]       = useState(0);
  const [swiping, setSwiping]     = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const startX = useRef(null);
  const DELETE_THRESHOLD = 72;

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    setSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (startX.current === null) return;
    const dx = e.touches[0].clientX - startX.current;
    if (dx < 0) {
      const clamped = Math.max(dx, -DELETE_THRESHOLD);
      setSwipeX(clamped);
      // Button erscheint sobald man anfängt zu wischen
      if (clamped < -8) setShowDelete(true);
    }
  };

  const handleTouchEnd = () => {
    // Einrasten wenn mehr als 60% gewischt
    if (swipeX < -DELETE_THRESHOLD * 0.6) {
      setSwipeX(-DELETE_THRESHOLD);
      setShowDelete(true);
    } else {
      setSwipeX(0);
      setShowDelete(false);
    }
    setSwiping(false);
    startX.current = null;
  };

  const handleReset = () => { setSwipeX(0); setShowDelete(false); };

  const bg   = done ? Z.primaryFixed : Z.surfaceContainerLowest;
  const bord = done ? Z.primaryFixedDim : Z.outlineVariant;

  return (
    <div style={{ position: "relative", borderRadius: 14, overflow: "hidden" }}>
      {/* Delete Button — erscheint live beim Wischen */}
      <div style={{
        position: "absolute", right: 0, top: 0, bottom: 0,
        width: DELETE_THRESHOLD,
        background: Z.error,
        display: "flex", alignItems: "center", justifyContent: "center",
        borderRadius: 14,
        opacity: showDelete ? 1 : 0,
        transition: swiping ? "none" : "opacity 0.15s ease",
      }}>
        <button onClick={() => onDelete(habit.id)} style={{ border: "none", background: "transparent", color: "#fff", fontSize: 20, cursor: "pointer", padding: "0 16px", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          🗑️
        </button>
      </div>

      {/* Habit Row */}
      <div
        onTouchStart={isCustom ? handleTouchStart : undefined}
        onTouchMove={isCustom ? handleTouchMove : undefined}
        onTouchEnd={isCustom ? handleTouchEnd : undefined}
        onClick={showDelete ? handleReset : undefined}
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: swiping ? "none" : "transform 0.25s ease",
          position: "relative", zIndex: 1,
        }}
      >
        <button
          onClick={showDelete ? handleReset : () => onToggle(habit.id)}
          style={{
            width: "100%", textAlign: "left", padding: "13px 16px", borderRadius: 14,
            border: `1px solid ${bord}`, cursor: "pointer",
            background: bg,
            display: "flex", alignItems: "center", gap: 12,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: "background 0.15s, border-color 0.15s",
          }}
        >
          <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, border: `1.5px solid ${done ? Z.primary : Z.outlineVariant}`, background: done ? Z.primary : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {done && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="1.5,5 4,7.5 8.5,2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          </div>
          <span style={{ fontSize: 14, fontWeight: done ? 400 : 500, color: done ? Z.onSurfaceVariant : Z.onSurface, textDecoration: done ? "line-through" : "none", opacity: done ? 0.7 : 1, flex: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {habit.label}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {streak >= 2 && (
              <span style={{ fontSize: 11, background: Z.primaryFixed, color: Z.primary, borderRadius: 8, padding: "2px 7px", fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", border: `1px solid ${Z.primaryFixedDim}` }}>
                🔥 {streak}
              </span>
            )}
            {habit.isHero && !done && (
              <span style={{ fontSize: 10, fontWeight: 600, background: Z.primary, color: Z.onPrimary, borderRadius: 6, padding: "2px 8px", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>HERO</span>
            )}
          </div>
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DRAGGABLE HABIT ITEM (Edit-Modus)
// ─────────────────────────────────────────────
function DraggableHabitItem({ habit, index, onRemove, onDragStart, onDragEnter, onDragEnd, isDragging, isOver }) {
  const touchStartY = useRef(null);
  const touchStartIdx = useRef(null);

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragEnter={() => onDragEnter(index)}
      onDragEnd={onDragEnd}
      onDragOver={e => e.preventDefault()}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 14px",
        background: isDragging ? Z.primaryFixed : isOver ? Z.surfaceContainerLow : Z.surfaceContainerLowest,
        borderRadius: 12,
        border: `1px solid ${isDragging ? Z.primary : isOver ? Z.primaryFixedDim : Z.outlineVariant}`,
        opacity: isDragging ? 0.5 : 1,
        transform: isOver ? "scale(1.01)" : "scale(1)",
        transition: "all 0.15s ease",
        cursor: "grab",
        userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none", touchAction: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        touchAction: "none",
        boxShadow: isDragging ? "0 8px 24px rgba(85,98,77,0.2)" : "none",
      }}
    >
      {/* Drag Handle */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3, flexShrink: 0, opacity: 0.4 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ display: "flex", gap: 3 }}>
            <div style={{ width: 3, height: 3, borderRadius: "50%", background: Z.onSurfaceVariant }} />
            <div style={{ width: 3, height: 3, borderRadius: "50%", background: Z.onSurfaceVariant }} />
          </div>
        ))}
      </div>

      <span style={{ flex: 1, fontSize: 13, color: Z.onSurface, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.4 }}>
        {habit.label}
      </span>

      <button
        onClick={() => onRemove(habit.id)}
        style={{ border: "none", background: "#fee2e2", borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}
      >
        🗑️
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// EDITABLE ROUTINE SECTION mit Drag & Drop
// ─────────────────────────────────────────────
function EditableRoutineSection({ title, theme, habits, customHabits, doneHabits, habitStreaks, onToggle, onDelete, onAdd, onGoToDiscover, routineId, onSave, onReset, hasEdits }) {
  const [editMode, setEditMode]     = useState(false);
  const [editHabits, setEditHabits] = useState([]);
  const [saving, setSaving]         = useState(false);
  const [dragIdx, setDragIdx]       = useState(null);
  const [overIdx, setOverIdx]       = useState(null);
  const isMorning = theme === "morning";

  const allHabits = [...habits, ...(customHabits || [])];

  const startEdit = () => {
    setEditHabits(allHabits.map(h => ({ ...h })));
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(theme, editHabits);
    setSaving(false);
    setEditMode(false);
  };

  const removeFromEdit = (id) => {
    setEditHabits(prev => prev.filter(h => h.id !== id));
  };

  // ── Drag & Drop Handler ───────────────────
  const handleDragStart = (idx) => setDragIdx(idx);

  const handleDragEnter = (idx) => {
    if (idx === dragIdx) return;
    setOverIdx(idx);
    setEditHabits(prev => {
      const arr = [...prev];
      const dragged = arr.splice(dragIdx, 1)[0];
      arr.splice(idx, 0, dragged);
      setDragIdx(idx);
      return arr;
    });
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  // ── Touch Drag & Drop (Mobile) ────────────
  const touchItem = useRef(null);
  const touchClone = useRef(null);
  const touchStartY = useRef(0);
  const itemRefs = useRef([]);

  const handleTouchStart = (e, idx) => {
    e.preventDefault(); // Verhindert Text-Selektion und Scroll
    touchItem.current = idx;
    touchStartY.current = e.touches[0].clientY;
    setDragIdx(idx);

    // Ghost Element erstellen
    const el = itemRefs.current[idx];
    if (!el) return;
    const clone = el.cloneNode(true);
    clone.style.cssText = `position:fixed;left:${el.getBoundingClientRect().left}px;top:${el.getBoundingClientRect().top}px;width:${el.offsetWidth}px;opacity:0.8;background:${Z.primaryFixed};border:2px solid ${Z.primary};border-radius:12px;z-index:9999;pointer-events:none;transform:scale(1.03);box-shadow:0 12px 32px rgba(85,98,77,0.25);`;
    document.body.appendChild(clone);
    touchClone.current = clone;
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const y = e.touches[0].clientY;
    if (touchClone.current) {
      const dy = y - touchStartY.current;
      touchClone.current.style.top = (parseFloat(touchClone.current.style.top) + dy) + "px";
      touchStartY.current = y;
    }

    // Über welchem Item befinden wir uns?
    const els = itemRefs.current;
    for (let i = 0; i < els.length; i++) {
      if (!els[i]) continue;
      const rect = els[i].getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) {
        if (i !== touchItem.current) {
          handleDragEnter(i);
          touchItem.current = i;
        }
        break;
      }
    }
  };

  const handleTouchEnd = () => {
    if (touchClone.current) {
      document.body.removeChild(touchClone.current);
      touchClone.current = null;
    }
    setDragIdx(null);
    setOverIdx(null);
    touchItem.current = null;
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>{isMorning ? "☀️" : "🌙"}</span>
          <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: 16, fontWeight: 500, color: Z.onSurface }}>{title}</span>
        </div>
        <button onClick={editMode ? cancelEdit : startEdit} style={{ fontSize: 11, color: editMode ? Z.outline : Z.primary, border: `1px solid ${editMode ? Z.outlineVariant : Z.primaryFixedDim}`, background: editMode ? "transparent" : Z.primaryFixed, borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>
          {editMode ? "Abbrechen" : "✏️ Bearbeiten"}
        </button>
      </div>

      {/* Edit Modus */}
      {editMode ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {editHabits.length === 0 && (
            <div style={{ padding: "20px", textAlign: "center", color: Z.outline, fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif", border: `1px dashed ${Z.outlineVariant}`, borderRadius: 12 }}>
              Keine Habits — füge welche über die Bibliothek hinzu
            </div>
          )}
          {editHabits.map((h, idx) => (
            <div
              key={h.id}
              ref={el => itemRefs.current[idx] = el}
              onTouchStart={e => handleTouchStart(e, idx)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <DraggableHabitItem
                habit={h}
                index={idx}
                onRemove={removeFromEdit}
                onDragStart={handleDragStart}
                onDragEnter={handleDragEnter}
                onDragEnd={handleDragEnd}
                isDragging={dragIdx === idx}
                isOver={overIdx === idx}
              />
            </div>
          ))}

          {/* Zurücksetzen + Speichern */}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            {hasEdits && (
              <button onClick={() => { onReset(theme); setEditMode(false); }} style={{ flex: 1, padding: "12px", borderRadius: 12, border: `1px solid ${Z.outlineVariant}`, background: "transparent", color: Z.onSurfaceVariant, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Zurücksetzen
              </button>
            )}
            <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "12px", borderRadius: 12, border: "none", background: Z.primary, color: Z.onPrimary, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {saving ? "Speichern…" : "✓ Speichern"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {allHabits.map((h) => (
              <HabitItem key={h.id} habit={h} done={doneHabits.includes(h.id)} streak={habitStreaks[h.id] || 0} onToggle={onToggle} onDelete={(id) => onDelete(id, theme)} theme={theme} isCustom={!!(customHabits || []).find(c => c.id === h.id)} />
            ))}
          </div>
          <AddHabitForm theme={theme} onAdd={(l) => onAdd(theme, l)} onGoToDiscover={onGoToDiscover} />
        </>
      )}
    </div>
  );
}
function AddHabitForm({ theme, onAdd, onGoToDiscover }) {
  const [open, setOpen]         = useState(false);
  const [value, setValue]       = useState("");
  const [suggestions, setSugg]  = useState([]);
  const isMorning = theme === "morning";

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    if (v.length >= 2) {
      // Bibliothek durchsuchen
      const results = searchLibrary(v).slice(0, 4);
      setSugg(results);
    } else {
      setSugg([]);
    }
  };

  const handleAdd = (label) => {
    const t = (label || value).trim();
    if (!t) return;
    onAdd(t);
    setValue("");
    setSugg([]);
    setOpen(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} style={{ width: "100%", marginTop: 10, padding: "11px 16px", borderRadius: 12, border: `1px dashed ${Z.primaryFixedDim}`, background: "transparent", color: Z.primary, fontSize: 13, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
          + Habit hinzufügen
        </button>
      )}

      {open && (
        <div style={{ marginTop: 10, position: "relative" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid ${Z.primary}`, background: Z.surfaceContainerLowest, color: Z.onSurface, fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none" }}
              placeholder="Habit suchen oder eingeben…"
              value={value}
              onChange={handleChange}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              autoFocus
            />
            <button onClick={() => handleAdd()} style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: Z.primary, color: Z.onPrimary, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>+</button>
            <button onClick={() => { setOpen(false); setValue(""); setSugg([]); }} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${Z.outlineVariant}`, background: "transparent", color: Z.outline, fontSize: 14, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>✕</button>
          </div>

          {/* Vorschläge */}
          {suggestions.length > 0 && (
            <div style={{ marginTop: 6, background: Z.surfaceContainerLowest, border: `1px solid ${Z.outlineVariant}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => handleAdd(s.label)} style={{ width: "100%", textAlign: "left", padding: "10px 14px", border: "none", background: "transparent", borderBottom: i < suggestions.length - 1 ? `1px solid ${Z.outlineVariant}` : "none", cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", alignItems: "center", gap: 10 }}
                  onMouseEnter={e => e.currentTarget.style.background = Z.surfaceContainerLow}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{s.icon || "🌿"}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: Z.onSurface }}>{s.label}</div>
                    {s.category && <div style={{ fontSize: 11, color: Z.outline }}>{s.category}</div>}
                  </div>
                  <span style={{ marginLeft: "auto", fontSize: 11, color: Z.primary, fontWeight: 600 }}>+ Hinzufügen</span>
                </button>
              ))}
              {/* In Bibliothek suchen */}
              <button onClick={() => { onGoToDiscover?.(); setOpen(false); setValue(""); setSugg([]); }} style={{ width: "100%", textAlign: "left", padding: "10px 14px", border: "none", background: Z.primaryFixed, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", alignItems: "center", gap: 8, color: Z.primary, fontSize: 13, fontWeight: 600 }}>
                <span>✨</span> Mehr in der Bibliothek entdecken →
              </button>
            </div>
          )}

          {/* Shortcut zur Bibliothek wenn keine Eingabe */}
          {value.length === 0 && (
            <button onClick={() => { onGoToDiscover?.(); setOpen(false); }} style={{ width: "100%", marginTop: 6, padding: "9px 14px", borderRadius: 10, border: `1px solid ${Z.outlineVariant}`, background: "transparent", color: Z.onSurfaceVariant, fontSize: 13, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", alignItems: "center", gap: 8 }}>
              <span>✨</span> Aus der Bibliothek wählen
            </button>
          )}
        </div>
      )}
    </>
  );
}


// ─────────────────────────────────────────────
// BIBLIOTHEK-SUCHE (client-seitig)
// ─────────────────────────────────────────────
function searchLibrary(query) {
  try {
    const q = query.toLowerCase();
    const results = [];
    for (const cat of HABIT_LIBRARY) {
      for (const sub of (cat.subcategories || [])) {
        for (const habit of (sub.habits || [])) {
          if (habit.label.toLowerCase().includes(q)) {
            results.push({ ...habit, icon: sub.icon || cat.icon, category: cat.label });
          }
          if (results.length >= 6) return results;
        }
      }
    }
    return results;
  } catch { return []; }
}

// ─────────────────────────────────────────────
// ROUTINE SELECTOR
// ─────────────────────────────────────────────
function RoutineSelector({ currentId, onSelect, onClose }) {
  const [activeCat, setActiveCat] = useState("custom");
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(25,28,24,0.5)", zIndex: 100, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div style={{ background: Z.surface, width: "100%", maxHeight: "88vh", borderRadius: "24px 24px 0 0", overflow: "hidden", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: Z.outlineVariant }} />
        </div>
        <div style={{ padding: "8px 20px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 20, fontWeight: 500, color: Z.onSurface, margin: 0 }}>Protokoll wählen</h2>
            <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: Z.outline }}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 8, paddingBottom: 16, overflowX: "auto" }}>
            {/* Eigene Routine immer zuerst */}
            <button onClick={() => setActiveCat("custom")} style={{ padding: "7px 16px", borderRadius: 20, border: `1px solid`, borderColor: activeCat === "custom" ? Z.primary : Z.outlineVariant, background: activeCat === "custom" ? Z.primary : "transparent", color: activeCat === "custom" ? Z.onPrimary : Z.onSurfaceVariant, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}>
              ✏️ Eigene
            </button>
            {Object.entries(ROUTINE_CATEGORIES).map(([key, { label, icon }]) => (
              <button key={key} onClick={() => setActiveCat(key)} style={{ padding: "7px 16px", borderRadius: 20, border: `1px solid`, borderColor: activeCat === key ? Z.primary : Z.outlineVariant, background: activeCat === key ? Z.primary : "transparent", color: activeCat === key ? Z.onPrimary : Z.onSurfaceVariant, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap", flexShrink: 0, display: "flex", alignItems: "center", gap: 5 }}>
                {icon} {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ overflowY: "auto", padding: "4px 20px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Eigene Routine Karte */}
          {activeCat === "custom" && (
            <button onClick={() => { onSelect("custom"); onClose(); }} style={{ textAlign: "left", padding: "16px", borderRadius: 16, border: `${currentId === "custom" ? "2px" : "1px"} solid ${currentId === "custom" ? Z.primary : Z.outlineVariant}`, background: currentId === "custom" ? Z.primaryFixed : Z.surfaceContainerLowest, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: currentId === "custom" ? Z.primaryFixedDim : Z.surfaceContainer, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>✏️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: Z.onSurface }}>Meine eigene Routine</span>
                    {currentId === "custom" && <span style={{ fontSize: 10, background: Z.primary, color: Z.onPrimary, borderRadius: 6, padding: "2px 7px", fontWeight: 600 }}>AKTIV</span>}
                  </div>
                  <div style={{ fontSize: 13, color: Z.onSurfaceVariant, lineHeight: 1.5 }}>Starte mit einer leeren Routine und füge nur die Habits hinzu die zu dir passen. Kein Protokoll, keine Vorgaben.</div>
                </div>
                <div style={{ fontSize: 11, color: Z.outline, flexShrink: 0, textAlign: "right" }}>
                  <div>0 ☀️</div>
                  <div>0 🌙</div>
                </div>
              </div>
            </button>
          )}
          {activeCat !== "custom" && getRoutinesByCategory(activeCat).map((r) => {
            const isActive = r.id === currentId;
            const isRole = r.category === "rolemodel";
            return (
              <button key={r.id} onClick={() => { onSelect(r.id); onClose(); }} style={{ textAlign: "left", padding: "16px", borderRadius: 16, border: `${isActive ? "2px" : "1px"} solid ${isActive ? Z.primary : Z.outlineVariant}`, background: isActive ? Z.primaryFixed : Z.surfaceContainerLowest, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: isActive ? Z.primaryFixedDim : Z.surfaceContainer, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{r.icon || "🌿"}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: Z.onSurface }}>{isRole ? (r.name.split("—")[1]?.trim() || r.name) : r.name}</span>
                      {isActive && <span style={{ fontSize: 10, background: Z.primary, color: Z.onPrimary, borderRadius: 6, padding: "2px 7px", fontWeight: 600 }}>AKTIV</span>}
                    </div>
                    {isRole && r.focus && <div style={{ fontSize: 11, color: Z.primary, fontWeight: 600, marginBottom: 4 }}>{r.focus}</div>}
                    <div style={{ fontSize: 13, color: Z.onSurfaceVariant, lineHeight: 1.5 }}>{r.description}</div>
                    {isRole && r.quote && (
                      <div style={{ marginTop: 8, padding: "8px 12px", background: Z.surfaceContainerLow, borderRadius: 8, borderLeft: `2px solid ${Z.primaryFixedDim}` }}>
                        <span style={{ fontSize: 12, color: Z.onSurfaceVariant, fontStyle: "italic" }}>„{r.quote}"</span>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: Z.outline, flexShrink: 0, textAlign: "right" }}>
                    <div>{r.morning?.length || 0} ☀️</div>
                    <div>{r.night?.length || 0} 🌙</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// BOTTOM NAV
// ─────────────────────────────────────────────
function BottomNav({ active, onChange, t }) {
  const tabs = [
    { id: "today",    icon: "🏠", label: t("nav_today") },
    { id: "agent",    icon: "✦",  label: t("nav_coach") },
    { id: "insights", icon: "📊", label: t("nav_insights") },
    { id: "discover", icon: "✨", label: t("nav_discover") },
  ];
  return (
    <nav style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", width: "90%", maxWidth: 420, background: "rgba(248,250,243,0.88)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderRadius: 999, padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-around", zIndex: 50, boxShadow: "0 8px 32px rgba(85,98,77,0.12)", border: `1px solid ${Z.outlineVariant}` }}>
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        return (
          <button key={tab.id} onClick={() => onChange(tab.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "8px 0", border: "none", background: "none", cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <span style={{ fontSize: 20 }}>{tab.icon}</span>
            <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 400, color: isActive ? Z.primary : Z.outline, letterSpacing: "0.04em" }}>{tab.label}</span>
            {isActive && <div style={{ width: 4, height: 4, borderRadius: "50%", background: Z.primary }} />}
          </button>
        );
      })}
    </nav>
  );
}

// ─────────────────────────────────────────────
// AVATAR
// ─────────────────────────────────────────────
function AvatarInitials({ name }) {
  const color = (() => { try { return JSON.parse(localStorage.getItem("habitOS_avatarColor")) || { bg: Z.primary, text: "#fff" }; } catch { return { bg: Z.primary, text: "#fff" }; } })();
  const initials = (name || "?").trim().split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div style={{ width: "100%", height: "100%", background: color.bg, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%" }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: color.text, fontFamily: "'Manrope', sans-serif" }}>{initials}</span>
    </div>
  );
}

// ─────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────
export default function App() {
  const { user, loading: authLoading, signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword, signOut } = useAuth();
  const { routineId, doneHabits, streak, freezes, earnedFreezeToday, history, customHabits, routineEdits, subscription, loading: dataLoading, toggleHabit, switchRoutine, addCustomHabit, removeCustomHabit, saveRoutineEdit, resetRoutineEdit } = useHabitOS(user?.id);
  const { isDark, toggle: toggleDark, tokens } = useDarkMode();
  const { lang, t, toggleLanguage } = useTranslation();

  // Z global updaten damit alle Sub-Komponenten die aktuelle Palette nutzen
  Z = tokens;

  const morningRef  = useRef(null);
  const [tab, setTab]               = useState("today");
  const [confettiFired, setCF]      = useState(false);
  const [showSelector, setSelector] = useState(false);
  const [showProfile, setProfile]   = useState(false);
  const [showPersonality, setShowPersonality] = useState(false);
  const [personalityState, setPersonalityState] = useState({ current: "friend", onChange: null });
  const [onboarded, setOnboarded]   = useState(() => localStorage.getItem("habitOS_onboarded") === "true");

  // Routine + Habits (werden auch nach frühen returns gebraucht → oben berechnen)
  const routine       = ROUTINES[routineId] || ROUTINES.neuro;
  const morningHabits = [...(routine.morning || []), ...(customHabits?.morning || [])];
  const nightHabits   = [...(routine.night   || []), ...(customHabits?.night   || [])];
  const allHabits     = [...morningHabits, ...nightHabits];

  // Hooks müssen VOR jedem return stehen (Rules of Hooks)
  const { insights } = useInsights(user?.id, allHabits);

  useEffect(() => {
    const last = localStorage.getItem("habitOS_lastDate");
    const t = today();
    if (last !== t) { setCF(false); localStorage.setItem("habitOS_lastDate", t); }
  }, []);

  if (authLoading) return <Loader text="Wird geladen…" />;
  if (!user) return <AuthGate onGoogle={signInWithGoogle} onLogin={signInWithEmail} onSignup={signUpWithEmail} onReset={resetPassword} />;
  if (dataLoading) return <Loader text="Deine Routinen werden geladen…" />;

  // Onboarding für neue User
  if (!onboarded) {
    return (
      <OnboardingFlow
        onComplete={(selectedRoutineId) => {
          if (selectedRoutineId && selectedRoutineId !== routineId) switchRoutine(selectedRoutineId);
          setOnboarded(true);
        }}
      />
    );
  }

  const progress      = allHabits.length ? Math.round((doneHabits.length / allHabits.length) * 100) : 0;
  const allMorningDone = morningHabits.length > 0 && morningHabits.every((h) => doneHabits.includes(h.id));
  const isRole        = routine.category === "rolemodel";
  const userName      = user.user_metadata?.full_name?.split(" ")[0] || "Du";

  // Agent habitData Objekt
  const habitData = {
    user, routineId, doneHabits, streak, freezes,
    allHabits, history, insights, recentMessages: [],
  };

  // Habit-Streaks berechnen
  const habitStreaks = computeHabitStreaks(history, allHabits.map((h) => h.id));

  const handleToggle = async (habitId) => {
    const { isComplete } = await toggleHabit(habitId, allHabits, morningHabits);
    if (isComplete && !confettiFired) { spawnConfetti(morningRef.current); setCF(true); }
  };

  return (
    <>
      <style>{`
        @keyframes zenithConfetti { 0%{opacity:1;transform:translateY(0) rotate(0deg)} 100%{opacity:0;transform:translateY(200px) rotate(540deg)} }
        * { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'Plus Jakarta Sans',sans-serif; background:${Z.surface}; }
        button:active { transform:scale(0.97); }
      `}</style>

      {showSelector && <RoutineSelector currentId={routineId} onSelect={switchRoutine} onClose={() => setSelector(false)} />}
      {showProfile  && <ProfileSheet user={user} streak={streak} freezes={freezes} subscription={subscription} isDark={isDark} onToggleDark={toggleDark} lang={lang} onToggleLanguage={toggleLanguage} onClose={() => setProfile(false)} onSignOut={() => { setProfile(false); signOut(); }} />}
      {showPersonality && <PersonalitySelector current={personalityState.current} onChange={personalityState.onChange} onClose={() => setShowPersonality(false)} />}
      <PWAInstallBanner />

      <div style={{ minHeight: "100vh", background: `linear-gradient(160deg,${Z.surface} 0%,${Z.surfaceContainerLow} 60%,${Z.secondaryContainer}33 100%)`, paddingBottom: 120 }}>

        {/* ── HEADER ── */}
        <header style={{ position: "sticky", top: 0, zIndex: 40, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: Z.surface, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: `1px solid ${Z.outlineVariant}44` }}>
          <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: 20, fontWeight: 500, color: Z.primary, letterSpacing: "-0.02em", flexShrink: 0 }}>Habit OS</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            {subscription === "pro" && <span style={{ fontSize: 11, background: Z.primary, color: Z.onPrimary, borderRadius: 8, padding: "3px 8px", fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>PRO</span>}
            <div onClick={() => setProfile(true)} title="Profil" style={{ width: 36, height: 36, borderRadius: "50%", overflow: "hidden", cursor: "pointer", background: Z.surfaceContainer, flexShrink: 0 }}>
              {user.user_metadata?.avatar_url
                ? <img src={user.user_metadata.avatar_url} alt="Profil" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <AvatarInitials name={user.user_metadata?.full_name || user.email} />
              }
            </div>
          </div>
        </header>

        <main style={{ maxWidth: 520, margin: "0 auto", padding: "0 20px" }}>

          {/* ── TODAY TAB ── */}
          {tab === "today" && (
            <>
              <section style={{ paddingTop: 32, paddingBottom: 24 }}>
                <h1 style={{ fontFamily: "'Manrope',sans-serif", fontSize: 36, fontWeight: 300, color: Z.primary, letterSpacing: "-0.03em", lineHeight: 1.15, margin: "0 0 6px" }}>
                  {getGreeting(t)},<br />{userName}
                </h1>
                <p style={{ fontSize: 14, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {t("greeting_subtitle")}
                </p>
              </section>

              {/* Bento: Progress + Streak */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div style={{ background: Z.surfaceContainerLowest, borderRadius: 24, padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, boxShadow: "0 4px 20px rgba(85,98,77,0.06)", border: `1px solid ${Z.outlineVariant}` }}>
                  <div style={{ position: "relative", width: 80, height: 80 }}>
                    <ProgressRing pct={progress} />
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Manrope',sans-serif", color: Z.primary }}>{progress}%</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontFamily: "'Plus Jakarta Sans',sans-serif", color: Z.outline, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Heute</span>
                  <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: 15, fontWeight: 500, color: Z.onSurface }}>{doneHabits.length} / {allHabits.length}</span>
                </div>
                <div style={{ background: Z.primary, borderRadius: 24, padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, boxShadow: "0 4px 20px rgba(85,98,77,0.15)" }}>
                  <span style={{ fontSize: 26 }}>🔥</span>
                  <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: 30, fontWeight: 600, color: Z.onPrimary, lineHeight: 1 }}>{streak}</span>
                  <span style={{ fontSize: 11, fontFamily: "'Plus Jakarta Sans',sans-serif", color: "rgba(255,255,255,0.7)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Tage</span>
                </div>
              </div>

              {/* Kalender */}
              <div style={{ background: Z.surfaceContainerLowest, borderRadius: 24, padding: "20px", marginBottom: 16, boxShadow: "0 4px 20px rgba(85,98,77,0.04)", border: `1px solid ${Z.outlineVariant}` }}>
                <div style={{ fontSize: 11, fontFamily: "'Plus Jakarta Sans',sans-serif", color: Z.outline, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600, marginBottom: 14 }}>
                  Letzte 7 Tage
                </div>
                <MiniCalendar history={history} />
                <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
                  {[["complete", Z.primary, "✓", "Vollständig"], ["freeze", "#3b82f6", "❄", "Freeze"], ["grace", "#f59e0b", "~", "Grace"], ["missed", Z.error, "✗", "Verpasst"]].map(([k, c, sym, l]) => (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <div style={{ width: 14, height: 14, borderRadius: 4, background: DAY_STATUS[k].bg, border: `1px solid ${DAY_STATUS[k].border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: c, fontWeight: 700 }}>{sym}</div>
                      <span style={{ fontSize: 11, color: Z.outline, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Freeze + Protokoll */}
              <div style={{ marginBottom: 14 }}><FreezeBar freezes={freezes} earnedToday={earnedFreezeToday} /></div>

              <button onClick={() => setSelector(true)} style={{ width: "100%", marginBottom: 16, padding: "14px 18px", borderRadius: 20, border: `1px solid ${Z.outlineVariant}`, background: Z.surfaceContainerLowest, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 2px 12px rgba(85,98,77,0.04)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: Z.primaryFixed, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{routine.icon || "🌿"}</div>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 11, color: Z.outline, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 2 }}>Protokoll</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: Z.onSurface }}>{isRole ? (routine.name.split("—")[1]?.trim() || routine.name) : routine.name}</div>
                    {isRole && routine.focus && <div style={{ fontSize: 12, color: Z.primary, fontWeight: 500 }}>{routine.focus}</div>}
                  </div>
                </div>
                <span style={{ color: Z.outline, fontSize: 18 }}>›</span>
              </button>

              {isRole && routine.quote && (
                <div style={{ marginBottom: 24, padding: "13px 16px", background: Z.secondaryContainer, borderRadius: 16, borderLeft: `3px solid ${Z.secondary}` }}>
                  <span style={{ fontSize: 13, color: Z.onSecondaryContainer, fontStyle: "italic", lineHeight: 1.6, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>„{routine.quote}"</span>
                </div>
              )}

              {/* Morning */}
              <section ref={morningRef} style={{ marginBottom: 20, position: "relative" }}>
                <EditableRoutineSection
                  title="Morning Routine"
                  theme="morning"
                  habits={morningHabits.filter(h => !(customHabits.morning || []).find(c => c.id === h.id))}
                  customHabits={customHabits.morning || []}
                  doneHabits={doneHabits}
                  habitStreaks={habitStreaks}
                  onToggle={handleToggle}
                  onDelete={(id, section) => removeCustomHabit(id, section)}
                  onAdd={(section, label) => addCustomHabit(section, label)}
                  onGoToDiscover={() => setTab("discover")}
                  routineId={routineId}
                  hasEdits={!!(routineEdits[routineId]?.morning?.length)}
                  onSave={async (section, base) => {
                    const current = routineEdits[routineId] || {};
                    await saveRoutineEdit(routineId,
                      section === "morning" ? base : (current.morning || morningHabits),
                      section === "night"   ? base : (current.night   || nightHabits),
                    );
                  }}
                  onReset={() => resetRoutineEdit(routineId)}
                />
                {allMorningDone && <div style={{ textAlign: "center", marginTop: 8 }}><span style={{ fontSize: 11, background: Z.primaryFixed, color: Z.primary, borderRadius: 20, padding: "4px 12px", fontWeight: 600, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Freeze verdient ❄️</span></div>}
              </section>

              {/* Night */}
              <section style={{ background: Z.inverseSurface, borderRadius: 28, padding: "24px 20px", marginBottom: 20 }}>
                <EditableRoutineSection
                  title="Night Routine"
                  theme="night"
                  habits={nightHabits.filter(h => !(customHabits.night || []).find(c => c.id === h.id))}
                  customHabits={customHabits.night || []}
                  doneHabits={doneHabits}
                  habitStreaks={habitStreaks}
                  onToggle={handleToggle}
                  onDelete={(id, section) => removeCustomHabit(id, section)}
                  onAdd={(section, label) => addCustomHabit(section, label)}
                  onGoToDiscover={() => setTab("discover")}
                  routineId={routineId}
                  hasEdits={!!(routineEdits[routineId]?.night?.length)}
                  onSave={async (section, base) => {
                    const current = routineEdits[routineId] || {};
                    await saveRoutineEdit(routineId,
                      section === "morning" ? base : (current.morning || morningHabits),
                      section === "night"   ? base : (current.night   || nightHabits),
                    );
                  }}
                  onReset={() => resetRoutineEdit(routineId)}
                />
              </section>
            </>
          )}

          {/* ── AGENT TAB ── */}
          {tab === "agent" && (
            <div style={{
              position: "fixed",
              top: 57,
              left: 0,
              right: 0,
              bottom: 76,
              display: "flex",
              flexDirection: "column",
              background: Z.surface,
              overflow: "hidden",
            }}>
              <ConsentGate userId={user.id} Z={Z}>
                <AgentChat userId={user.id} habitData={habitData} onShowPersonalitySelector={(current, onChange) => { setPersonalityState({ current, onChange }); setShowPersonality(true); }} />
              </ConsentGate>
            </div>
          )}

          {/* ── INSIGHTS TAB ── */}
          {tab === "insights" && (
            <InsightsTab userId={user.id} allHabits={allHabits} />
          )}

          {/* ── DISCOVER TAB ── */}
          {tab === "discover" && (
            <section style={{ paddingTop: 28 }}>
              <DiscoverTab onAddHabit={(section, label) => addCustomHabit(section, label)} />
            </section>
          )}

        </main>

        <BottomNav active={tab} onChange={setTab} t={t} />
      </div>
    </>
  );
}

function Loader({ text }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg,#f8faf3 0%,#f2f4ed 50%,#fed7d244 100%)` }}>
      <div style={{ fontSize: 32, marginBottom: 16 }}>🌿</div>
      <span style={{ fontSize: 14, color: "#757870", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{text}</span>
    </div>
  );
}